<template>
  <div class="grid grid-cols-12 gap-4">
    <div v-for="(block, bi) in blocks" :key="bi" :style="{ gridColumn: `span ${block.width || 12} / span ${block.width || 12}` }" class="min-w-0">
      <ReportBlocks
        v-if="block.type === 'stack'"
        :blocks="block.blocks.map((b) => ({ ...b, width: 12 }))"
        :output="output"
        :trend="trend"
        :entity-id="entityId"
        @open-quote="$emit('open-quote', $event)"
      />

      <div v-else-if="block.type === 'gauge'" class="flex flex-col items-center">
        <svg viewBox="0 0 160 92" class="w-44">
          <path d="M 12 84 A 68 68 0 0 1 148 84" fill="none" stroke="#E8E9E9" stroke-width="12" stroke-linecap="round" />
          <path
            d="M 12 84 A 68 68 0 0 1 148 84"
            fill="none"
            :stroke="gaugeColor(block)"
            stroke-width="12"
            stroke-linecap="round"
            :stroke-dasharray="`${arc * gaugeShare(block)} ${arc}`"
          />
          <text x="80" y="74" text-anchor="middle" class="fill-gray-900" style="font-size: 34px; font-weight: 700">{{ fmtNumber(get(block.field)) }}</text>
        </svg>
        <div v-if="block.band_field" class="text-xs font-semibold uppercase tracking-wide mt-1" :style="{ color: gaugeColor(block) }">{{ label(block.band_labels, get(block.band_field)) }}</div>
        <div class="text-[11px] text-gray-500">{{ block.label }}</div>
        <div v-if="block.stats" class="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-4 text-sm w-full">
          <template v-for="(st, si) in block.stats" :key="si">
            <span class="text-gray-500">{{ st.label }}</span>
            <span class="text-right font-medium text-gray-900 tabular-nums">{{ stat(st) }}</span>
          </template>
        </div>
      </div>

      <div v-else-if="block.type === 'stats'" class="flex flex-wrap gap-x-8 gap-y-1">
        <div v-for="(st, si) in block.items" :key="si">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{{ st.label }}</div>
          <div class="text-sm font-medium text-gray-900 sensitive">{{ stat(st) }}</div>
        </div>
      </div>

      <div v-else-if="block.type === 'badges'" class="flex flex-wrap items-center gap-2">
        <span
          v-for="(bd, di) in block.items.filter((b) => get(b.field) != null && get(b.field) !== '')"
          :key="di"
          class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
          :style="pill(bd.colors && bd.colors[get(bd.field)])"
        >{{ label(bd.labels, get(bd.field)) }}</span>
      </div>

      <template v-else-if="block.type === 'text'">
        <div v-if="get(block.field) && get(block.field) !== block.hide_if" :class="block.box ? 'rounded-lg border border-gray-900 p-4' : ''">
          <h4 v-if="block.title" class="heading-subsection mb-2">{{ block.title }}</h4>
          <div v-if="block.label && block.emphasis" class="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">{{ block.label }}</div>
          <p class="sensitive" :class="block.emphasis ? 'text-[15px] leading-6 text-gray-900' : 'text-sm text-gray-700'" data-demo-blur>
            <span v-if="block.label && !block.emphasis" class="font-semibold text-gray-900">{{ block.label }}. </span>{{ get(block.field) }}
          </p>
        </div>
      </template>

      <div v-else-if="block.type === 'stages'">
        <div class="flex">
          <div v-for="(stage, si) in block.stages" :key="stage" class="flex-1 min-w-0">
            <div
              class="h-2 mr-0.5"
              :class="[si === 0 ? 'rounded-l-full' : '', si === block.stages.length - 1 ? 'rounded-r-full' : '', si <= block.stages.indexOf(get(block.value_field)) ? 'bg-neutral-700' : 'bg-gray-200']"
            ></div>
            <div class="mt-1.5 text-[11px] truncate" :class="stage === get(block.value_field) || stage === get(block.compare_field) ? 'font-semibold text-gray-900' : 'text-gray-500'">{{ stage }}</div>
            <div class="text-[10px] leading-tight">
              <span v-if="stage === get(block.compare_field)" class="text-gray-500">{{ block.compare_label }}</span>
              <span v-if="stage === get(block.compare_field) && stage === get(block.value_field)" class="text-gray-500"> · </span>
              <span v-if="stage === get(block.value_field)" class="text-neutral-800">{{ block.value_label }}</span>
            </div>
          </div>
        </div>
        <div v-if="block.warn_field && get(block.warn_field)" class="mt-2 flex gap-2 rounded-md bg-primary-50 border border-primary-200 px-3 py-2 text-sm text-primary-900">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mt-0.5 text-primary-600" />
          <span class="sensitive" data-demo-blur>{{ get(block.warn_text_field) }}</span>
        </div>
      </div>

      <div v-else-if="block.type === 'trend'" class="flex flex-col h-full">
        <div class="text-xs font-semibold text-gray-500">{{ block.label }}</div>
        <svg viewBox="0 0 220 64" class="w-full mt-2">
          <line v-for="(g, gi) in block.guides || []" :key="gi" x1="0" x2="220" :y1="trendY(g.value, block)" :y2="trendY(g.value, block)" :stroke="COLORS[g.color] || GRAY" stroke-dasharray="2 3" stroke-width="1" opacity="0.6" />
          <polyline v-if="trendPoints(block).length > 1" :points="trendPoints(block).map((p) => p.join(',')).join(' ')" fill="none" stroke="#385883" stroke-width="2" />
          <circle v-for="(p, pi) in trendPoints(block)" :key="pi" :cx="p[0]" :cy="p[1]" r="3" fill="#385883" />
        </svg>
        <div class="text-[11px] text-gray-500 mt-1">{{ trend.length }} run{{ trend.length === 1 ? '' : 's' }}<span v-if="trend.length"> since {{ fmtDate(trend[0].date) }}</span></div>
      </div>

      <div v-else-if="block.type === 'list' && !(block.hide_empty && !listItems(block).length)">
        <div v-if="block.title" class="flex items-baseline justify-between mb-2">
          <h4 class="heading-subsection">{{ block.title }}</h4>
          <span class="text-[11px] text-gray-500">{{ listItems(block).length }}</span>
        </div>
        <div v-if="block.mix && listItems(block).length" class="flex h-1.5 mb-2 rounded-full overflow-hidden">
          <div v-for="m in mix(block)" :key="m.key" :style="{ width: m.pct + '%', background: COLORS[m.color] || GRAY }"></div>
        </div>
        <div v-if="!listItems(block).length" class="text-sm text-gray-400">None.</div>
        <ol :class="block.columns === 2 ? 'grid grid-cols-2 gap-x-8 gap-y-3' : 'space-y-3'">
          <li v-for="(it, ii) in listItems(block)" :key="ii" class="flex gap-2.5 text-sm min-w-0">
            <span v-if="block.numbered" class="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">{{ ii + 1 }}</span>
            <font-awesome-icon
              v-if="block.item.icon && block.item.icon.map[it[block.item.icon.field]]"
              :icon="['fas', block.item.icon.map[it[block.item.icon.field]].icon]"
              class="mt-1 text-[11px] shrink-0"
              :style="{ color: COLORS[block.item.icon.map[it[block.item.icon.field]].color] }"
            />
            <span v-if="block.item.dot" class="mt-1.5 w-2 h-2 rounded-full shrink-0" :style="{ background: COLORS[block.item.dot.colors[it[block.item.dot.field]]] || GRAY }"></span>
            <span
              v-if="block.item.badge"
              class="self-start inline-flex justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold shrink-0 min-w-[76px]"
              :style="pill(block.item.badge.colors && block.item.badge.colors[it[block.item.badge.field]])"
            >{{ label(block.item.badge.labels, it[block.item.badge.field]) }}</span>
            <div class="min-w-0 flex-1">
              <div v-if="block.item.kicker" class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{{ label(block.item.kicker_labels, it[block.item.kicker]) }}</div>
              <div v-if="block.item.title" class="font-medium text-gray-900 sensitive" data-demo-blur>{{ it[block.item.title] }}</div>
              <div v-if="block.item.subtext && it[block.item.subtext]" class="text-[12px] text-gray-500 sensitive" data-demo-blur>{{ block.item.subtext_prefix || '' }}{{ it[block.item.subtext] }}</div>
              <div v-if="block.item.text && it[block.item.text]" class="sensitive" :class="block.item.title ? 'text-[13px] text-gray-600 mt-0.5' : 'text-gray-900'" data-demo-blur>{{ it[block.item.text] }}</div>
              <div v-if="block.item.note && it[block.item.note]" class="sensitive mt-1.5 text-xs text-gray-700 bg-gray-50 rounded-md px-2.5 py-1.5" data-demo-blur>
                <span v-if="block.item.note_prefix" class="font-semibold text-gray-900">{{ block.item.note_prefix }}</span> {{ it[block.item.note] }}
              </div>
              <button
                v-for="(ev, qi) in (block.item.quote ? it[block.item.quote] || [] : []).slice(0, block.item.quote_limit || 3)"
                :key="'q' + qi"
                type="button"
                class="sensitive block text-left text-[12px] italic leading-5 text-gray-500 hover:text-accent transition-colors mt-0.5"
                data-demo-blur
                title="Open in the transcript"
                @click="$emit('open-quote', ev)"
              >“{{ ev.quote }}”</button>
              <div v-if="block.item.nodes && (it[block.item.nodes] || []).length" class="flex flex-wrap gap-1 mt-1.5">
                <router-link
                  v-for="n in it[block.item.nodes]"
                  :key="n.id"
                  :to="{ path: `/entity/${entityId}/engagement-tracker`, query: { node: n.id } }"
                  class="inline-flex items-center gap-1 max-w-full rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-700 hover:border-accent hover:text-accent transition-colors"
                  :title="`${TYPE_LABEL[n.type] || n.type} · ${n.status}`"
                >
                  <font-awesome-icon :icon="['fas', TYPE_ICON[n.type] || 'circle-info']" class="text-[9px] shrink-0" />
                  <span class="truncate sensitive">{{ n.title }}</span>
                </router-link>
              </div>
            </div>
          </li>
        </ol>
      </div>

      <div v-else-if="block.type === 'table' && !(block.hide_empty && !(get(block.field) || []).length)">
        <h4 v-if="block.title" class="heading-subsection mb-2">{{ block.title }}</h4>
        <table class="w-full text-[13px]">
          <thead>
            <tr class="text-xs text-gray-500 border-b border-gray-200">
              <th v-for="c in block.columns" :key="c.field" class="font-medium py-2 px-3 first:pl-0 last:pr-0" :class="c.align === 'right' ? 'text-right' : 'text-left'">{{ c.label_field ? get(c.label_field) : c.label }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(row, ri) in get(block.field) || []" :key="ri">
              <td
                v-for="c in block.columns"
                :key="c.field"
                class="sensitive py-2 px-3 first:pl-0 last:pr-0"
                :class="[c.align === 'right' ? 'text-right tabular-nums' : 'text-left', c.emphasis ? 'font-semibold text-gray-900 bg-gray-50' : 'text-gray-800']"
                data-demo-blur
              >
                {{ row[c.field] }}
                <div v-if="c.sub_field && row[c.sub_field]" class="text-xs text-gray-500 font-normal">{{ row[c.sub_field] }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="block.type === 'bullets' && !(block.hide_empty && !(get(block.field) || []).length)">
        <h4 v-if="block.title" class="heading-subsection mb-1.5">{{ block.title }}</h4>
        <ul class="list-disc pl-5 space-y-1 text-sm text-gray-800">
          <li v-for="(b, bi2) in get(block.field) || []" :key="bi2" class="sensitive" data-demo-blur>{{ b }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { TYPE_META } from '@/components/EngagementTracker/engagementTrackerHelpers'

const GRAY = '#B3B4B5'
const COLORS = { green: '#389A6C', yellow: '#E0A040', red: '#D53D3D', gray: GRAY, navy: '#385883', orange: '#CC5F38' }
const TINTS = {
  green: ['#E3F3E0', '#282C27'], yellow: ['#FDF1DC', '#7A5214'], red: ['#FDECEC', '#D53D3D'], gray: ['#F1F1F1', '#626264'],
  navy: ['#EBF4FF', '#21426C'], orange: ['#FEE3D9', '#723B28'],
}

export default {
  name: 'ReportBlocks',
  components: { FontAwesomeIcon },
  props: {
    blocks: { type: Array, required: true },
    output: { type: Object, required: true },
    trend: { type: Array, default: () => [] },
    entityId: { type: Number, required: true },
  },
  emits: ['open-quote'],
  data() {
    return {
      arc: Math.PI * 68,
      COLORS,
      GRAY,
      TYPE_ICON: Object.fromEntries(Object.entries(TYPE_META).map(([k, v]) => [k, v.icon])),
      TYPE_LABEL: Object.fromEntries(Object.entries(TYPE_META).map(([k, v]) => [k, v.label])),
    }
  },
  methods: {
    get(path) {
      let node = this.output
      for (const part of String(path || '').split('.')) {
        if (node === null || node === undefined) return null
        node = Array.isArray(node) ? node[Number(part)] : node[part]
      }
      return node === undefined ? null : node
    },
    label(map, key) {
      return (map && map[key]) || (key === null || key === undefined ? '' : String(key).replace(/_/g, ' '))
    },
    pill(color) {
      const [bg, fg] = TINTS[color] || TINTS.gray
      return { background: bg, color: fg }
    },
    gaugeShare(block) {
      const v = Number(this.get(block.field))
      return Number.isFinite(v) ? Math.max(0, Math.min(1, v / (block.max || 100))) : 0
    },
    gaugeColor(block) {
      const v = Number(this.get(block.field))
      const bands = block.bands || [{ min: 60, color: 'green' }, { min: 35, color: 'yellow' }, { min: -Infinity, color: 'red' }]
      const hit = bands.find((b) => v >= b.min)
      return COLORS[hit ? hit.color : 'gray']
    },
    stat(st) {
      let v = this.get(st.field)
      if ((v === null || v === '') && st.fallback_field) return this.label(st.fallback_labels, this.get(st.fallback_field))
      if (v === null || v === '') return st.empty || ''
      if (st.format === 'percent') return `${Math.round(Number(v))}%`
      if (st.format === 'month') {
        const [y, m] = String(v).split('-').map(Number)
        return m ? new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : String(v)
      }
      if (st.format === 'date') return this.fmtDate(v)
      return this.label(st.labels, v)
    },
    fmtNumber(v) {
      const n = Number(v)
      return Number.isFinite(n) ? Math.round(n) : '—'
    },
    fmtDate(d) {
      if (!d) return ''
      const [y, m, day] = String(d).slice(0, 10).split('-').map(Number)
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    trendY(v, block) {
      return 58 - (Math.max(0, Math.min(block.max || 100, v)) * 52) / (block.max || 100)
    },
    trendPoints(block) {
      const pts = this.trend.filter((p) => p.value !== null && p.value !== undefined)
      if (!pts.length) return []
      if (pts.length === 1) return [[110, this.trendY(pts[0].value, block)]]
      return pts.map((p, i) => [8 + (i * 204) / (pts.length - 1), this.trendY(p.value, block)])
    },
    listItems(block) {
      const items = [...(this.get(block.field) || [])]
      if (block.sort) {
        const order = block.sort.order || []
        items.sort((a, b) => order.indexOf(a[block.sort.field]) - order.indexOf(b[block.sort.field]))
      }
      return items
    },
    mix(block) {
      const items = this.listItems(block)
      return block.mix.order.map((key) => ({ key, color: block.mix.colors[key], pct: (100 * items.filter((i) => i[block.mix.field] === key).length) / items.length }))
    },
  },
}
</script>
